/**
 * Phone verification modal component.
 */

import { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

interface PhoneVerificationModalProps {
  show: boolean;
  onHide: () => void;
}

export default function PhoneVerificationModal({ show, onHide }: PhoneVerificationModalProps) {
  const { refreshUser } = useAuth();
  const [countryCode, setCountryCode] = useState('+1');
  const [areaCode, setAreaCode] = useState('');
  const [phonePart1, setPhonePart1] = useState('');
  const [phonePart2, setPhonePart2] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Combine phone number parts into E.164 format
  const getFullPhoneNumber = (): string => {
    const area = areaCode.replace(/\D/g, '');
    const part1 = phonePart1.replace(/\D/g, '');
    const part2 = phonePart2.replace(/\D/g, '');
    // Extract digits from country code (keep + if present, but we'll add it)
    const countryDigits = countryCode.replace(/[^\d]/g, '');

    if (!countryDigits || !area || !part1 || !part2) {
      return '';
    }

    // Return in E.164 format: +[country][area][part1][part2]
    return `+${countryDigits}${area}${part1}${part2}`;
  };

  const handleSendCode = async () => {
    const fullPhoneNumber = getFullPhoneNumber();
    if (!fullPhoneNumber) {
      toast.error('Please enter a complete phone number');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.requestUserPhoneVerification(fullPhoneNumber);
      if (response.error) {
        toast.error(response.error);
        return;
      }
      setCodeSent(true);
      toast.success('Verification code sent');
    } catch (error) {
      toast.error('Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    const fullPhoneNumber = getFullPhoneNumber();
    setIsLoading(true);
    try {
      const response = await apiClient.confirmUserPhoneVerification(verificationCode, fullPhoneNumber);
      if (response.error) {
        toast.error(response.error);
        return;
      }
      toast.success('Phone number verified successfully');
      await refreshUser();
      handleClose();
    } catch (error) {
      toast.error('Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCountryCode('+1');
    setAreaCode('');
    setPhonePart1('');
    setPhonePart2('');
    setVerificationCode('');
    setCodeSent(false);
    onHide();
  };

  // Format input handlers to only allow digits
  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Ensure it starts with +
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/[^\d]/g, '');
    } else {
      value = '+' + value.slice(1).replace(/[^\d]/g, '');
    }
    setCountryCode(value);
  };

  const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setAreaCode(value);
    // Auto-focus next field when 3 digits entered
    if (value.length === 3 && phonePart1.length < 3) {
      const nextInput = document.getElementById('phone-part-1') as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handlePhonePart1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setPhonePart1(value);
    // Auto-focus next field when 3 digits entered
    if (value.length === 3 && phonePart2.length < 4) {
      const nextInput = document.getElementById('phone-part-2') as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handlePhonePart2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPhonePart2(value);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Verify Your Phone Number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <i className="fas fa-info-circle me-2"></i>
          Your phone number must be verified to place orders. Orders will remain in Pending state
          until your phone is verified.
        </Alert>
        <Form.Group className="mb-3">
          <Form.Label>Phone Number</Form.Label>
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div style={{ width: '120px' }}>
              <Form.Label className="small text-muted mb-1">Country Code</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+1"
                value={countryCode}
                onChange={handleCountryCodeChange}
                disabled={codeSent}
                autoComplete="tel-country-code"
                name="phone-country-code"
                maxLength={5}
              />
            </div>
            <div style={{ width: '100px' }}>
              <Form.Label className="small text-muted mb-1">Area Code</Form.Label>
              <Form.Control
                type="tel"
                id="phone-area-code"
                placeholder="647"
                value={areaCode}
                onChange={handleAreaCodeChange}
                disabled={codeSent}
                autoComplete="tel-area-code"
                name="phone-area-code"
                maxLength={3}
              />
            </div>
            <div style={{ width: '100px' }}>
              <Form.Label className="small text-muted mb-1">Number</Form.Label>
              <Form.Control
                type="tel"
                id="phone-part-1"
                placeholder="685"
                value={phonePart1}
                onChange={handlePhonePart1Change}
                disabled={codeSent}
                autoComplete="tel-local"
                name="phone-part-1"
                maxLength={3}
              />
            </div>
            <div style={{ width: '120px' }}>
              <Form.Label className="small text-muted mb-1">&nbsp;</Form.Label>
              <Form.Control
                type="tel"
                id="phone-part-2"
                placeholder="6647"
                value={phonePart2}
                onChange={handlePhonePart2Change}
                disabled={codeSent}
                autoComplete="tel-local-suffix"
                name="phone-part-2"
                maxLength={4}
              />
            </div>
          </div>
          <Form.Text className="text-muted">
            Example: +1 647 685 6647
          </Form.Text>
        </Form.Group>
        {codeSent && (
          <Form.Group className="mb-3">
            <Form.Label>Verification Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter 6-digit verification code"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              autoComplete="one-time-code"
              name="verification-code"
            />
            <Form.Text>Enter the code sent to your phone</Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} className="me-2">
          Cancel
        </Button>
        {!codeSent ? (
          <Button variant="primary" onClick={handleSendCode} disabled={isLoading}>
            Send Verification Code
          </Button>
        ) : (
          <Button variant="success" onClick={handleVerifyCode} disabled={isLoading}>
            Verify Code
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
